import { useEffect, useRef } from 'react';

/**
 * The `'infinite'` mode footer, rendered inside `<tbody>` so it scrolls with
 * the rows rather than pinning below them.
 *
 * Two affordances, on purpose:
 *
 * - an `IntersectionObserver` sentinel that fires ~200px early, so the next
 *   chunk is usually already in flight by the time the user reaches the
 *   bottom; and
 * - a real, visible "Load more" button.
 *
 * The button is not a nicety. A bare sentinel is unreachable by keyboard, is
 * invisible to anyone who does not scroll with a pointer, and inside the
 * expanded overlay's own scroll container the observer can simply never fire.
 * The button is the contract; the sentinel is the optimisation.
 *
 * The observer uses the viewport as its root rather than the table's scroll
 * container: intersection is clipped by every scrolling ancestor anyway, and
 * `DataTable` does not own a ref to that element in every configuration.
 */
const SENTINEL_ROOT_MARGIN = '200px 0px';

export interface LoadMoreRowProps {
    colSpan: number;
    /** More rows exist server-side. `false` renders nothing. */
    hasMore: boolean;
    /** A fetch for the next chunk is in flight. */
    isLoadingMore: boolean;
    onLoadMore: () => void;
    label: string;
}

export function LoadMoreRow({
    colSpan,
    hasMore,
    isLoadingMore,
    onLoadMore,
    label
}: LoadMoreRowProps) {
    const sentinelRef = useRef<HTMLTableRowElement>(null);
    // The observer callback must not re-subscribe every time the parent
    // re-renders with a new closure.
    const onLoadMoreRef = useRef(onLoadMore);
    onLoadMoreRef.current = onLoadMore;

    useEffect(() => {
        if (!hasMore || isLoadingMore) {
            return;
        }
        const node = sentinelRef.current;
        if (!node) {
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    onLoadMoreRef.current();
                }
            },
            { rootMargin: SENTINEL_ROOT_MARGIN }
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [hasMore, isLoadingMore]);

    if (!hasMore) {
        return null;
    }

    return (
        <>
            <tr ref={sentinelRef} aria-hidden="true">
                <td colSpan={colSpan} className="h-px p-0" />
            </tr>
            <tr>
                <td colSpan={colSpan} className="px-3 py-3 text-center">
                    <button
                        type="button"
                        onClick={onLoadMore}
                        disabled={isLoadingMore}
                        className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-1.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {isLoadingMore && (
                            <span
                                aria-hidden="true"
                                className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600"
                            />
                        )}
                        {isLoadingMore ? 'Loading more…' : label}
                    </button>
                </td>
            </tr>
        </>
    );
}
